
import { Weight, Pill } from 'lucide-react';
import { Medication } from './index';

export const weightLossMedications: Medication[] = [
  {
    id: '1',
    name: 'Semaglutide',
    description: 'A GLP-1 receptor agonist that helps regulate appetite and blood sugar levels. It promotes satiety and reduces cravings, making it an effective tool for weight loss and blood sugar management.',
    research: 'Semaglutide and Obesity - Semaglutide, a GLP-1 receptor agonist, significantly reduces body weight by enhancing satiety and reducing appetite in obese patients. A 2021 trial showed an average 14.9% weight loss over 68 weeks, alongside improved cardiometabolic markers.',
    url: 'https://www.nejm.org/doi/full/10.1056/NEJMoa2032183',
    category: 'weight-loss',
    Icon: Weight,
    video: 'aIUPQ8K7FTI',
    availablePreparations: 'Injection (e.g., Semaglutide/Vitamin B12, Semaglutide/Vitamin B6), Sublingual Troches (e.g., 1.5 mg, 3 mg, 5.1 mg, 7.2 mg, 9.3 mg).',
    clinicalDetails: {
      dosingGuidelines: 'Initial dose 0.25mg SC weekly for 4 weeks, then 0.5mg weekly for 4 weeks. May increase to 1mg weekly if needed. Maximum dose 2.4mg weekly for weight management.',
      administrationProtocols: 'Administer subcutaneously in abdomen, thigh, or upper arm. Rotate injection sites. Store pen refrigerated (36-46°F) and allow to warm to room temperature before use.',
      contraindications: 'Personal/family history of medullary thyroid carcinoma or MEN 2, pregnancy, severe gastrointestinal disease, diabetic retinopathy, pancreatitis.',
      monitoringRequirements: 'Monitor for pancreatitis, hypoglycemia (if used with sulfonylureas), gallbladder disease, and renal impairment. Regular thyroid monitoring advised.'
    },
    adverseReactions: {
      common: 'Nausea, vomiting, diarrhea, constipation, abdominal pain.',
      lessCommon: 'Pancreatitis, gallbladder disease, hypoglycemia (when used with insulin or sulfonylureas).',
      rare: 'Thyroid C-cell tumors (observed in animal studies), allergic reactions.'
    }
  },
  {
    id: '2',
    name: 'Tirzepatide',
    description: 'A dual GIP and GLP-1 receptor agonist that enhances insulin sensitivity, reduces appetite, and supports weight loss. Used for obesity management and type 2 diabetes control.',
    research: 'Tirzepatide for Weight Loss - Tirzepatide, a dual GIP/GLP-1 agonist, outperforms other weight loss drugs, with trials showing up to 20.9% weight reduction in 72 weeks. It improves insulin sensitivity and glycemic control, making it a breakthrough for obesity and diabetes.',
    url: 'https://www.nejm.org/doi/full/10.1056/NEJMoa2206038',
    category: 'weight-loss',
    Icon: Weight,
    video: 'AmNWCGxHT6I',
    availablePreparations: 'Injection (e.g., Tirzepatide/Vitamin B12, Tirzepatide/Vitamin B6), Sublingual Troches (e.g., 13.4 mg, 20 mg, 26.7 mg, 33.3 mg, 40 mg, 46.7 mg).',
    clinicalDetails: {
      dosingGuidelines: 'Initial dose 2.5mg SC weekly for 4 weeks, then 5mg weekly. Increase by 2.5mg every 4 weeks to target dose of 10-15mg weekly.',
      administrationProtocols: 'Administer subcutaneously in abdomen, thigh, or upper arm. Rotate injection sites. May be administered without regard to meals. Store pen refrigerated (36-46°F).',
      contraindications: 'History of pancreatitis, personal/family history of MTC or MEN2, pregnancy, severe gastrointestinal disease, diabetic retinopathy.',
      monitoringRequirements: 'Monitor for hypoglycemia, pancreatitis, and renal function. Evaluate for diabetic retinopathy and thyroid C-cell tumors periodically.'
    },
    adverseReactions: {
      common: 'Nausea, diarrhea, vomiting, decreased appetite.',
      lessCommon: 'Hypoglycemia (when used with insulin), injection site reactions.',
      rare: 'Pancreatitis, allergic reactions.'
    }
  },
  {
    id: '3',
    name: 'Phentermine',
    description: 'A stimulant that suppresses appetite by affecting neurotransmitters in the brain. Commonly used for short-term weight loss in overweight patients.',
    research: 'Phentermine Review - Phentermine, a sympathomimetic amine, suppresses appetite via norepinephrine release. It\'s effective for short-term weight loss (3-6 months), with studies showing 5-10% body weight reduction, though long-term safety is limited.',
    url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC8295716/?utm_source=chatgpt.com',
    category: 'weight-loss',
    Icon: Pill,
    video: 'GML54Unogj0',
    availablePreparations: 'Oral Capsules (e.g., 37.5 mg).',
    clinicalDetails: {
      dosingGuidelines: 'Typical dose 15-37.5mg orally once daily, taken before breakfast or 1-2 hours after breakfast. Short-term use only (8-12 weeks).',
      administrationProtocols: 'Take early in the day to prevent insomnia. Avoid taking with other CNS stimulants or weight-loss medications unless specifically prescribed together.',
      contraindications: 'Cardiovascular disease, hyperthyroidism, glaucoma, agitated states, history of drug abuse, MAOIs within 14 days, pregnancy, breastfeeding, hypertension.',
      monitoringRequirements: 'Regular monitoring of blood pressure, heart rate, and sleep patterns. Periodic assessment for psychological dependence and tolerance.'
    },
    adverseReactions: {
      common: 'Insomnia, dry mouth, constipation, increased heart rate, hypertension.',
      lessCommon: 'Palpitations, restlessness, dependence.',
      rare: 'Pulmonary hypertension, valvular heart disease.'
    }
  },
  {
    id: '4',
    name: 'Tesofensine',
    description: 'A potent appetite suppressant that enhances dopamine, serotonin, and noradrenaline levels to promote weight loss by reducing food intake and increasing metabolism.',
    research: 'Tesofensine in Obesity - Tesofensine, a triple monoamine reuptake inhibitor, reduces appetite and boosts metabolism. Phase II trials report 10% weight loss over 24 weeks, but its development is stalled due to cardiovascular safety concerns.',
    url: 'https://www.thelancet.com/journals/lancet/article/PIIS0140-6736%2809%2960432-3/fulltext?utm_source=chatgpt.com',
    category: 'weight-loss',
    Icon: Pill,
    video: 'RxV1T3ayU2M',
    availablePreparations: 'Oral Capsules (e.g., 250 mcg, 500 mcg, 1 mg).',
    clinicalDetails: {
      dosingGuidelines: 'Investigational dosing ranges from 0.25-1.0mg daily, with 0.5mg daily showing optimal balance of efficacy and tolerability in trials.',
      administrationProtocols: 'Oral administration, taken at the same time each day. Adherence to prescribed lifestyle modifications required for optimal results.',
      contraindications: 'Uncontrolled hypertension, cardiovascular disease, psychiatric disorders, seizures, narrow-angle glaucoma, concomitant MAOI or SSRI use.',
      monitoringRequirements: 'Close monitoring of cardiovascular parameters (BP, HR), psychiatric symptoms, and dry mouth. Regular assessment of weight loss efficacy.'
    },
    adverseReactions: {
      common: 'Insomnia, dry mouth, nausea, constipation.',
      lessCommon: 'Increased heart rate, hypertension, anxiety.',
      rare: 'Cardiovascular events, psychiatric disorders.'
    }
  },
  {
    id: '5',
    name: 'Topiramate',
    description: 'An anticonvulsant that supports weight loss by altering appetite regulation and promoting satiety. Often used in combination with phentermine.',
    research: 'Topiramate for Weight Loss - Topiramate, an anticonvulsant, promotes weight loss (5-7% in trials) by altering appetite and taste perception. It\'s often combined with phentermine (Qsymia), enhancing efficacy, though cognitive side effects are notable.',
    url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC3626409/?utm_source=chatgpt.com',
    category: 'weight-loss',
    Icon: Pill,
    video: 'dQB2MCmaf_c',
    availablePreparations: 'Oral Tablets (e.g., 25 mg, 100 mg).',
    clinicalDetails: {
      dosingGuidelines: 'For weight loss, typically 25-100mg daily, often with slow titration. In Qsymia, doses range from 23-92mg topiramate with 3.75-15mg phentermine.',
      administrationProtocols: 'Gradual dose titration to minimize side effects. Take with or without food consistently. Evening dosing may help manage cognitive side effects.',
      contraindications: 'Pregnancy (Category D), metabolic acidosis, recent alcohol use, glaucoma, kidney stones, suicidal ideation, and severe hepatic impairment.',
      monitoringRequirements: 'Monitor cognitive function, mood changes, kidney function, serum bicarbonate (metabolic acidosis), and glaucoma symptoms. Regular pregnancy testing if applicable.'
    },
    adverseReactions: {
      common: 'Paresthesia (tingling sensation), cognitive dysfunction (e.g., memory issues), fatigue.',
      lessCommon: 'Kidney stones, metabolic acidosis, glaucoma.',
      rare: 'Suicidal ideation, severe skin reactions.'
    }
  }
];
